/**
 * Feature flag constants.
 *
 * These feature flags are managed in WorkOS and used to control access
 * to various features in the application.
 *
 * Values must match the feature flag names configured in WorkOS.
 */
export const FeatureFlag = {
  // Intake workflow feature flags
  INTAKE_PDF_STAMP: 'intake-pdf-stamp',
  INTAKE_SEE_AI_EXPLANATION: 'intake-see-ai-explanation',
  INTAKE_AI_SCHEDULING: 'intake-ai-scheduling',

  // Medical coding feature flags
  MEDICAL_CODING_ONLY_DOCUMENT_BASED: 'only-document-based-coding',
  // RogerDoc-only (GET /rogerdoc/feature_flags — not configured in WorkOS)
  ALLOW_RERUN_COMPLETED_CODING: 'allow-rerun-completed-coding',
  MEDICAL_CODING_CONSIDER_THERAPY_EVAL: 'medical-coding-consider-therapy-eval',
  MEDICAL_CODING_SHOW_FORCE_CODING_RERUN_BUTTON:
    'medical-coding-show-force-coding-rerun-button',
  MEDICAL_CODING_CHECK_NEW_DOCS_CRON: 'medical_coding_check_new_docs_cron',
  // Reporting feature flags
  ADOPTION_REPORT_BY_BRANCH: 'adoption-report-by-branch',
  REPORT_BUILDER: 'report-builder',
  ADVANCED_REPORTING: 'advanced-reporting',

  // Admin feature flags
  ADMIN_WORKSHEET_GOALS: 'admin-worksheet-goals',
  // RogerDoc-only: goals progress tracking feature
  USE_WORKSHEET_GOALS_PROGRESS: 'use_worksheet_goals_progress',
  // Billing-config fields (frequency, charge type, PDGM, etc.) in the
  // Add/Edit Insurance popup
  EDIT_INSURANCE_BILLING: 'edit-insurance-billing',
  // Visit type costs configuration in Admin panel
  ADMIN_TAB_BILLING_VIEW: 'admin-tab-billing-view',

  // Order Manager feature flags
  ORDER_MANAGER: 'order-manager',

  // OASIS Manager feature flags
  OASIS_MANAGER: 'oasis-manager',

  // Form schema versioning — use stored FormSchemaVersion documents from
  // Roger-Docs-API instead of the legacy question_db pipeline.
  VERSIONED_FORM_SCHEMAS: 'versioned-form-schemas',

  // Live Scrubber QA findings — gates the backend `contradictions` response and
  // the QA tab that renders it.
  CONTRADICTION_RULES_ENABLED: 'contradiction-rules-enabled',

  // Show full roles options in the clinician manager UI
  // For non-EMR users they don't need to see the clinic level roles
  SHOW_CLINIC_INFO_IN_USER_MANAGER: 'show-clinic-info-in-user-manager',
  // If true, the user can see roles not assigned to them in the invite/add user dialogs.
  // When false, only roles already assigned to the current user are shown.
  SHOW_UNASSIGNED_ROLES: 'show-unassigned-roles',

  // Product update modal ("What's New") in the app header
  PRODUCT_UPDATE_MODAL_ENABLED: 'product-update-modal-enabled',

  // Payroll Manager screens — Unpaid/Paid tabs (EMR-517). One shared flag
  // gates the sidebar item and every /payroll endpoint.
  PAYROLL_MANAGER: 'payroll-manager',
} as const;

export const PermissionSlug = {
  NOA_MANAGER: 'noa-manager:view',
  GOALS_ADMIN_TAB: 'goals:admin-tab',
  ORDER_MANAGER: 'order-manager:view',
  OASIS_MANAGER: 'oasis-manager:view',
  CAHPS_MANAGER: 'cahps-manager:view',
  PATIENT_MANAGER: 'patient-mgr:access',
  CLINICIAN_MANAGER: 'clinician-mgr:access',
  REPORTS_ACCESS: 'reports:access',
  ROGER_EMR_REPORTS_ACCESS: 'roger-emr-reports:access',
  REPORTS_VISITS: 'reports:visits',
  REPORTS_EPISODES: 'reports:episodes',
  REPORTS_PATIENTS: 'reports:patients',
  REPORTS_CLAIMS: 'reports:claims',
  BILLING_ACCESS: 'billing:view',
  QA_ACCESS: 'qa:access',
  HOTBOX_ACCESS: 'hotbox:access',
  ADMIN_SUPER_USER: 'admin:super-user',
  EXTERNAL_ADMIN_ACCESS: 'external-admin:access',
  INTAKE_ACCESS: 'intake:access',
  CODING_ACCESS: 'coding:access',
  CODING_FORCE_RERUN: 'coding:force-rerun',
  ROGERDOC_DASHBOARD_MANAGE: 'rogerdoc-dashboard:manage',
  CUSTOMIZATIONS_ACCESS: 'customizations:access',
  EMR_MANAGE: 'emr:manage',
  PAYROLL_ACCESS: 'payroll:access',
} as const;

export type FeatureFlagType = (typeof FeatureFlag)[keyof typeof FeatureFlag];
